
import dayjs from 'dayjs'
import { defineComponent, PropType } from 'vue'
import type { GroupedUsageMetric } from '~/types/types'
import { getThemeColors } from '~/utils/charts/zchart'
import baseColors from '~/utils/tailwind/colors'

const SERIES_COLOR_MAP: Record<string, string> = {
  secrets: 'aqua-500',
  standard_tier: 'juniper-500',
  advanced_tier: 'juniper-11'
}

const SERIES_LABEL_MAP: Record<string, string> = {
  secrets: 'Secrets Detection',
  standard_tier: 'Standard',
  advanced_tier: 'Advanced'
}

const DEFAULT_SERIES_COLOR = 'juniper-500'

const themeColors = getThemeColors(baseColors)

// Stack order (bottom → top): secrets, advanced, standard
// Legend reverses this: standard → advanced → secrets
const SERIES_ORDER: Record<string, number> = {
  secrets: 0,
  advanced_tier: 1,
  standard_tier: 2
}

function orderedSeries(metric: GroupedUsageMetric | null): GroupedUsageMetric['series'] {
  if (!metric?.series?.length) return []
  return [...metric.series].sort(
    (a, b) => (SERIES_ORDER[a.name] ?? 99) - (SERIES_ORDER[b.name] ?? 99)
  )
}

function formatDateLabel(isoDate: string): string {
  const parsed = dayjs(isoDate)
  return parsed.isValid() ? parsed.format('MMM D') : isoDate
}

export default defineComponent({
  name: 'UsageMetricChart',
  props: {
    metric: { type: Object as PropType<GroupedUsageMetric | null>, default: null },
    loading: { type: Boolean, default: false },
    label: { type: String, required: true },
    height: { type: Number, default: 250 },
    formatTotal: { type: Function as PropType<(total: number) => string>, required: true },
    formatMarker: { type: Function as PropType<(value: number) => string>, default: null },
    tooltipOptions: { type: Object, default: null },
    legendSubtitles: {
      type: Object as PropType<Record<string, string> | null>,
      default: null
    }
  },
  computed: {
    series(): GroupedUsageMetric['series'] {
      return orderedSeries(this.metric)
    },
    hasData(): boolean {
      return this.series.some((s) => s.values.some((v) => v > 0))
    },
    chartLabels(): string[] {
      return this.metric?.labels ?? []
    },
    firstLabel(): string {
      return this.chartLabels.length ? formatDateLabel(this.chartLabels[0]) : ''
    },
    lastLabel(): string {
      return this.chartLabels.length > 1
        ? formatDateLabel(this.chartLabels[this.chartLabels.length - 1])
        : ''
    },
    chartDataSets(): Array<{ name: string; values: number[] }> {
      return this.series.map((s) => ({
        name: SERIES_LABEL_MAP[s.name] ?? s.name,
        values: [...s.values]
      }))
    },
    chartColors(): string[] {
      if (!this.series.length) return [DEFAULT_SERIES_COLOR]
      return this.series.map((s) => SERIES_COLOR_MAP[s.name] ?? DEFAULT_SERIES_COLOR)
    },
    formattedTotal(): string {
      const total = this.metric?.series?.reduce((sum, s) => sum + (s.total ?? 0), 0) ?? 0
      return this.formatTotal(total)
    },
    chartMarkers() {
      if (!this.series.length || !this.chartLabels.length) return null
      let max = 0
      for (let i = 0; i < this.chartLabels.length; i++) {
        let stackedSum = 0
        for (const s of this.series) {
          stackedSum += s.values[i] ?? 0
        }
        if (stackedSum > max) max = stackedSum
      }
      if (!max) return null
      const formatter = this.formatMarker ?? this.formatTotal
      const topSeries = this.series[this.series.length - 1]
      const markerColor = SERIES_COLOR_MAP[topSeries?.name] ?? DEFAULT_SERIES_COLOR
      return [
        {
          label: formatter(max),
          value: max,
          options: { stroke: markerColor, labelColor: markerColor, lineType: 'dashed' }
        }
      ]
    },
    chartLegend(): Array<{ key: string; name: string; color: string }> {
      return this.series
        .map((s) => {
          const token = SERIES_COLOR_MAP[s.name] ?? DEFAULT_SERIES_COLOR
          return {
            key: s.name,
            name: SERIES_LABEL_MAP[s.name] ?? s.name,
            color: themeColors[token] ?? token
          }
        })
        .reverse()
    },
    mergedTooltipOptions() {
      return { ...this.tooltipOptions, reverseOrder: true }
    },
    chartAxisOptions() {
      return {
        yGridLine: false,
        yTickLabels: false,
        xTickValues: [],
        margin: { top: 20, right: 0, bottom: 6, left: 0 },
        padding: { left: 0, right: 0 },
        autoMargin: false,
        tickTextColor: '#c0c1c3'
      }
    },
    chartBarOptions() {
      return { barPadding: 0.05, stacked: true }
    }
  }
})
